import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import ChallengeCard from "@/components/ChallengeCard";
import SolutionCard from "@/components/SolutionCard";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Users, FileText, TrendingDown, Globe, Brain, Heart, HelpCircle, ArrowRight } from "lucide-react";
import { useContentSnippets } from "@/hooks/useContentSnippets";
import { BedarfsAnalysatorDialog } from "@/components/BedarfsAnalysatorDialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import PublicImpactCard from "@/components/public/PublicImpactCard";
import Modul0Entry from "@/components/landing/Modul0Entry";
import DualPathSelector from "@/components/landing/DualPathSelector";
import DozentenDuo from "@/components/landing/DozentenDuo";

const Index = () => {
  const { getSnippet, loading } = useContentSnippets();
  const [showBedarfsAnalysator, setShowBedarfsAnalysator] = useState(false);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "MAIP - Management Agiler Innovations-Projekte in der Pflege",
    "url": "https://www.maip.info",
    "logo": "https://www.maip.info/hero-image.jpg",
    "description": "Praxisorientierte KI-Weiterbildung für Führungskräfte und Nachwuchstalente in der Pflege",
    "sameAs": ["https://www.linkedin.com/company/maip-info"],
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="MAIP | KI-Weiterbildung für Führungskräfte in der Pflege"
        description="Befähigen Sie sich und Ihr Team für die Zukunft. MAIP ist das praxisorientierte Programm, um KI-Projekte in der ambulanten & stationären Pflege erfolgreich zu managen."
        canonical="https://www.maip.info/"
        schema={organizationSchema}
      />
      <Header />

      {/* 1. Hero */}
      <Hero />

      {/* 2. Modul 0 – kostenfreier Einstieg (Lead-Magnet) */}
      <Modul0Entry />

      {/* 3. Challenges */}
      <section id="challenges" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-muted/50 rounded-2xl p-8 md:p-12">
            <h2 className="text-center text-foreground mb-12">
              {loading ? "Kennen Sie diese Herausforderungen?" : getSnippet("home_challenges_intro", "Kennen Sie diese Herausforderungen?")}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <ChallengeCard
                icon={Users}
                title={loading ? "Fachkräftemangel" : getSnippet("home_challenge_1_title", "Fachkräftemangel")}
                description={loading ? "Der demografische Wandel und der Personalmangel setzen Ihre Teams unter enormen Druck." : getSnippet("home_challenge_1_desc", "Der demografische Wandel und der Personalmangel setzen Ihre Teams unter enormen Druck.")}
                iconColor="text-destructive"
              />
              <ChallengeCard
                icon={FileText}
                title={loading ? "Dokumentationslast" : getSnippet("home_challenge_2_title", "Dokumentationslast")}
                description={loading ? "Wertvolle Zeit für den Menschen geht durch wachsende bürokratische Anforderungen verloren." : getSnippet("home_challenge_2_desc", "Wertvolle Zeit für den Menschen geht durch wachsende bürokratische Anforderungen verloren.")}
                iconColor="text-premium"
              />
              <ChallengeCard
                icon={TrendingDown}
                title={loading ? "Kostendruck" : getSnippet("home_challenge_3_title", "Kostendruck")}
                description={loading ? "Steigende Kosten erfordern effizientere Prozesse, ohne die Qualität der Versorgung zu gefährden." : getSnippet("home_challenge_3_desc", "Steigende Kosten erfordern effizientere Prozesse, ohne die Qualität der Versorgung zu gefährden.")}
                iconColor="text-accent"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 4. DUAL-PATH SELECTOR – Das Herzstück (direkt nach Challenges) */}
      <DualPathSelector />

      {/* 5. Dozenten-Duo – Trust Section */}
      <DozentenDuo />

      {/* 6. Solutions */}
      <section id="solutions" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-center text-foreground mb-4">
            {loading ? "Ihr Rüstzeug für die digitale Transformation mit MAIP" : getSnippet("home_toolbox_h2", "Ihr Rüstzeug für die digitale Transformation mit MAIP")}
          </h2>
          <p className="text-center text-muted-foreground mb-16 max-w-3xl mx-auto text-lg">
            {loading
              ? "Ob erfahrene Führungskraft oder ambitionierter Nachwuchs – MAIP gibt Ihnen die Werkzeuge, um Ihre Ideen in erfolgreiche KI-Projekte zu verwandeln."
              : getSnippet("home_intro_text", "Ob erfahrene Führungskraft oder ambitionierter Nachwuchs – MAIP gibt Ihnen die Werkzeuge, um Ihre Ideen in erfolgreiche KI-Projekte zu verwandeln und als Innovator sichtbar zu werden.")}
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <SolutionCard
              icon={Globe}
              title={loading ? "Cultural Onboarding" : getSnippet("home_toolbox_kachel1_title", "Cultural Onboarding")}
              description={loading ? "Integrieren Sie internationale Fachkräfte schneller. Unsere KI-Audio-Stories überwinden Sprachbarrieren und vermitteln deutsche Pflegestandards empathisch." : getSnippet("home_solution_1_desc", "Integrieren Sie internationale Fachkräfte schneller. Unsere KI-Audio-Stories überwinden Sprachbarrieren und vermitteln deutsche Pflegestandards empathisch.")}
              iconColor="text-primary"
            />
            <SolutionCard
              icon={Brain}
              title={loading ? "Praxis-Kompetenz: KI in der Dokumentation" : getSnippet("home_solution_2_title", "Praxis-Kompetenz: KI in der Dokumentation")}
              description={loading ? "Wir zeigen Ihren Fachkräften, wie sie KI-Technologie sicher nutzen, um Berichte effizienter zu schreiben und Bürokratie abzubauen." : getSnippet("home_solution_2_desc", "Wir zeigen Ihren Fachkräften, wie sie KI-Technologie sicher nutzen, um Berichte effizienter zu schreiben und Bürokratie abzubauen. Hilfe zur Selbsthilfe.")}
              iconColor="text-accent"
            />
            <SolutionCard
              icon={Heart}
              title={loading ? "Menschenzentriertes Veränderungsmanagement" : getSnippet("home_toolbox_kachel3_title", "Menschenzentriertes Veränderungsmanagement")}
              description={loading ? "Gewinnen Sie Ihre Mitarbeitenden für den Wandel und gestalten Sie die Transformation verantwortungsvoll." : getSnippet("home_solution_3_desc", "Gewinnen Sie Ihre Mitarbeitenden für den Wandel und gestalten Sie die Transformation verantwortungsvoll.")}
              iconColor="text-primary"
            />
          </div>
        </div>
      </section>

      {/* 7. Impact Card */}
      <section className="py-12 bg-background">
        <div className="max-w-4xl mx-auto px-6">
          <PublicImpactCard />
        </div>
      </section>

      {/* 8. Bedarfs-Analysator CTA */}
      <section className="py-16 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <Card className="border border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5 hover:shadow-lg transition-all duration-300">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                    <HelpCircle className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <Badge variant="secondary" className="mb-2 text-xs">Orientierungshilfe</Badge>
                  <h2 className="text-2xl md:text-3xl font-bold mb-3 text-foreground">
                    Unsicher, welches Modul das richtige für Sie ist?
                  </h2>
                  <p className="text-base text-muted-foreground mb-4">
                    Beantworten Sie 3 kurze Fragen zu Ihrer Rolle und Ihren Zielen. Unser Bedarfs-Analysator empfiehlt Ihnen in 60 Sekunden den perfekten Lernpfad.
                  </p>
                  <Button size="lg" onClick={() => setShowBedarfsAnalysator(true)}>
                    3-Minuten-Check starten
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 9. Premium-Module: Datenschutz & Mitbestimmung */}
      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              {loading ? "Erweitern Sie Ihr Wissen: Unsere Online-Vertiefungsmodule" : getSnippet("home_premium_h2", "Erweitern Sie Ihr Wissen: Unsere Online-Vertiefungsmodule")}
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              {loading ? "Praxisorientierte, selbstgesteuerte Lernmodule für spezifische Kompetenzbereiche" : getSnippet("home_premium_p", "Praxisorientierte, selbstgesteuerte Lernmodule für spezifische Kompetenzbereiche")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Datenschutz-Kompass */}
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 md:p-8 border border-primary/20 flex flex-col hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-5">
                <div className="inline-block px-3 py-1 bg-primary/20 rounded-full mb-3">
                  <span className="text-xs font-bold text-primary">Premium-Modul</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2">
                  {loading ? "Datenschutz-Kompass" : getSnippet("home_datenschutz_title", "Datenschutz-Kompass")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {loading ? "Ihr Weg zur DSGVO-Konformität – 10 interaktive Lektionen mit Checklisten, Vorlagen und Zertifikat" : getSnippet("home_datenschutz_desc", "Ihr Weg zur DSGVO-Konformität – 10 interaktive Lektionen mit Checklisten, Vorlagen und Zertifikat")}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-5 mt-auto">
                {[{ v: "10", l: "Lektionen" }, { v: "6-8h", l: "Lerndauer" }, { v: "128€", l: "zzgl. MwSt." }].map((s) => (
                  <div key={s.l} className="bg-background/80 backdrop-blur rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-primary mb-1">{s.v}</div>
                    <div className="text-xs text-muted-foreground">{s.l}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                <Link to="/datenschutz-kompass-info">
                  <Button size="lg" className="w-full">Mehr erfahren & kaufen</Button>
                </Link>
                <Link to="/datenschutz-kompass">
                  <Button size="lg" variant="outline" className="w-full">Kostenlose Lektion testen</Button>
                </Link>
              </div>
            </div>

            {/* KI & Mitbestimmung */}
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 md:p-8 border border-primary/20 flex flex-col hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-5">
                <div className="inline-block px-3 py-1 bg-primary/20 rounded-full mb-3">
                  <span className="text-xs font-bold text-primary uppercase">Neu für MAV & BR</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2">
                  {loading ? "KI & Mitbestimmung: Vom Blockierer zum Gestalter" : getSnippet("home_mitbestimmung_title", "KI & Mitbestimmung: Vom Blockierer zum Gestalter")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {loading ? "Lernen Sie, wie Sie als MAV konstruktiv KI-Projekte mitgestalten statt blockieren." : getSnippet("home_mitbestimmung_desc", "Lernen Sie, wie Sie als MAV konstruktiv KI-Projekte mitgestalten statt blockieren und eine partnerschaftliche Rolle einnehmen.")}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-5 mt-auto">
                {[{ v: "6", l: "Lektionen" }, { v: "6-7h", l: "Lerndauer" }, { v: "149€", l: "zzgl. MwSt." }].map((s) => (
                  <div key={s.l} className="bg-background/80 backdrop-blur rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-primary mb-1">{s.v}</div>
                    <div className="text-xs text-muted-foreground">{s.l}</div>
                  </div>
                ))}
              </div>
              <Link to="/mitbestimmung-info">
                <Button size="lg" className="w-full">Mehr erfahren & buchen</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 10. Potenzial-Analysator */}
      <section className="py-16 px-6 bg-gradient-to-br from-primary/10 to-accent/10">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
            {loading ? "Entfesseln Sie das Potenzial: Was KI für Ihre Einrichtung leisten kann" : getSnippet("home_potential_h2", "Entfesseln Sie das Potenzial: Was KI für Ihre Einrichtung leisten kann")}
          </h2>
          <p className="text-base text-muted-foreground mb-6 max-w-3xl mx-auto">
            {loading
              ? "Administrative Last kostet mehr als nur Zeit – sie kostet bares Geld. Berechnen Sie in 90 Sekunden, welches finanzielle Potenzial Sie durch die Optimierung von Dokumentation, Planung und Kommunikation heben können."
              : getSnippet("home_potential_p1", "Administrative Last kostet mehr als nur Zeit – sie kostet bares Geld. Berechnen Sie in 90 Sekunden, welches finanzielle Potenzial Sie heben können.")}
          </p>
          <Link to="/potenzial-analyse">
            <Button size="lg">
              {loading ? "Jetzt Potenzial kostenlos analysieren" : getSnippet("home_potential_cta", "Jetzt Potenzial kostenlos analysieren")}
            </Button>
          </Link>
        </div>
      </section>

      {/* 11. Gestaltungsrat */}
      <section className="py-16 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-xl border border-primary/20 p-6 md:p-8 hover:shadow-lg transition-all">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1 text-center md:text-left">
                <div className="inline-block px-3 py-1 bg-primary/20 rounded-full mb-3">
                  <span className="text-primary font-bold text-xs uppercase tracking-wide">
                    {loading ? "Ihre Praxis-Idee ist gefragt!" : getSnippet("home_gestaltungsrat_badge", "Ihre Praxis-Idee ist gefragt!")}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-3 text-foreground">
                  {loading ? "Gestalten Sie mit: Ihre Impulse für die Pflege von morgen" : getSnippet("home_impuls_h2", "Gestalten Sie mit: Ihre Impulse für die Pflege von morgen")}
                </h2>
                <p className="text-base text-muted-foreground mb-4">
                  {loading
                    ? "Was ist Ihre größte Herausforderung im Alltag? Ihr Feedback hilft uns, praxisnahe KI-Lösungen zu entwickeln."
                    : getSnippet("home_gestaltungsrat_p", "Was ist Ihre größte Herausforderung im Alltag? Ihr Feedback hilft uns, praxisnahe KI-Lösungen zu entwickeln, die wirklich funktionieren.")}
                </p>
                <Link to="/wissen/gestaltungsrat">
                  <Button size="lg">
                    {loading ? "Impuls teilen" : getSnippet("home_gestaltungsrat_cta", "Impuls teilen")}
                  </Button>
                </Link>
              </div>
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                  <Users className="w-10 h-10 text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BedarfsAnalysatorDialog open={showBedarfsAnalysator} onOpenChange={setShowBedarfsAnalysator} />
      <Footer />
    </div>
  );
};

export default Index;
